import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import apiCalls from '../../utils/apiCalls';
import utils from '../../utils/utils';
import PropTypes from 'prop-types';
import CustomPaginator from '../../components/paginator';
import AdminOrdersTable from './adminOrdersTable';
import Helmet from 'react-helmet';

const RESULTS_PER_PAGE = 50;

const AdminOrders = ({ setLoading, setError }) => {
  const [sortBy, setSortBy] = useState(null);
  const [orderBatches, setOrderBatches] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  const [typedQuery, setTypedQuery] = useState(null); // for detecting input change
  const [query, setQuery] = useState(null); // for triggering actual search
  const queryTimer = useRef(null);

  useEffect(() => {
    clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => {
      setQuery(typedQuery);
    }, 1000);
  }, [typedQuery]);

  useEffect(() => {
    getOrderBatches();
  }, [query]);

  function getOrderBatches(newSortParam, newActivePage) {
    const pageNumber = newActivePage || activePage;

    setLoading(true);
    apiCalls
      .getOrderBatches({
        query,
        sortBy: newSortParam || sortBy,
        max: RESULTS_PER_PAGE,
        offset: (pageNumber - 1) * RESULTS_PER_PAGE,
      })
      .then((result) => {
        setOrderBatches(result.data.orderBatches);
        setTotalCount(result.data.totalCount);
        setLoading(false);
      })
      .catch((errors) => {
        setLoading(false);
        setError(utils.getErrorMessageFromResponse(errors));
      });
  }

  function setSortMethod(newSortBy) {
    let sortParam;

    switch (newSortBy) {
      case 'screenName':
        sortParam = sortBy === 'screenNameAsc' ? 'screenNameDesc' : 'screenNameAsc';
        break;
      case 'userId':
        sortParam = sortBy === 'userIdAsc' ? 'userIdDesc' : 'userIdAsc';
        break;
      case 'marketplaceId':
        sortParam = sortBy === 'marketplaceIdAsc' ? 'marketplaceIdDesc' : 'marketplaceIdAsc';
        break;
      case 'totalSum':
        sortParam = sortBy === 'totalSumAsc' ? 'totalSumDesc' : 'totalSumAsc';
        break;
      case 'email':
        sortParam = sortBy === 'finalSumAsc' ? 'finalSumDesc' : 'finalSumAsc';
        break;
      case 'dateCreated':
        sortParam = sortBy === 'dateCreatedAsc' ? 'dateCreatedDesc' : 'dateCreatedAsc';
        break;
      case 'id':
      default:
        sortParam = sortBy === 'idAsc' ? 'idDesc' : 'idAsc';
    }

    setSortBy(sortParam);
    getOrderBatches(sortParam);
  }

  function onPageSelect(newNumber) {
    if (newNumber !== activePage) {
      setActivePage(newNumber);
      getOrderBatches(undefined, newNumber);
    }
  }
  const { formatMessage } = useIntl();

  return (
    <div>
      <Helmet>
        <title>{formatMessage({ id: 'admin.orders.title' })}</title>
      </Helmet>

      <h3>
        <FormattedMessage id={'admin.orders.title'} />
      </h3>

      <div className="m-b-3 m-t-3">
        <CustomPaginator
          activePage={activePage}
          resultsPerPage={RESULTS_PER_PAGE}
          setActivePage={onPageSelect}
          totalCount={totalCount}
        />
      </div>

      <div className="text-right">
        <div className="form-group field field-string d-inline-block">
          <input
            className="form-control"
            placeholder="Search"
            type="text"
            value={typedQuery}
            onChange={(e) => setTypedQuery(e.target.value)}
          />
          <p className="help-block">
            <FormattedMessage id={'admin.orders.search.help'} />
          </p>
        </div>
      </div>
      <AdminOrdersTable orderBatches={orderBatches} getOrderBatches={getOrderBatches} setSortMethod={setSortMethod} />

      <div className="m-b-6">
        <CustomPaginator
          activePage={activePage}
          resultsPerPage={RESULTS_PER_PAGE}
          setActivePage={onPageSelect}
          totalCount={totalCount}
        />
      </div>
    </div>
  );
};

AdminOrders.propTypes = {
  setLoading: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

AdminOrders.defaultProps = {};

export default AdminOrders;
